import { type Ref, computed, ref } from "vue";

import { type PaginationData } from "shared/components/Pagination";

export type SelectionStateOptions<Item, Id> = {
  selectedIds: Ref<Set<Id>>;
  allItems: Ref<Map<Id, Item>>;
  pagination: Ref<PaginationData>;
};

export type SelectionStateReturn<Id> = {
  isAllSelected: Ref<boolean>;
  isIndeterminate: Ref<boolean>;
  toggleSelectAll: (selectAll: boolean) => void;
  toggleItemSelection: (id: Id, isSelected: boolean) => void;
};

export function useSelectionState<Item, Id>({
  selectedIds,
  allItems,
  pagination,
}: SelectionStateOptions<Item, Id>): SelectionStateReturn<Id> {
  const localPage = ref<PaginationData>({
    page: 1,
    pageCount: pagination.value.pageCount,
    pageSize: pagination.value.pageSize,
  });

  const isAllSelected = computed<boolean>(() => {
    if (!allItems.value.size || !selectedIds.value.size) return false;

    return Array.from(allItems.value.keys()).every((key) =>
      selectedIds.value.has(key)
    );
  });

  const isIndeterminate = computed<boolean>(() => {
    if (!selectedIds.value.size) return false;

    const currentPageIds = Array.from(allItems.value.keys());

    const selectedCurrentPageItemsCount = currentPageIds.filter((id) =>
      selectedIds.value.has(id)
    ).length;

    // If none of the current page items are selected, return false
    if (selectedCurrentPageItemsCount === 0) return false;

    // If all of the current page items are selected, return false
    if (selectedCurrentPageItemsCount === currentPageIds.length) return false;

    // Otherwise, some but not all items on the current page are selected
    return true;
  });

  function toggleSelectAll(selectAll: boolean): void {
    const currentPageIds = new Set(allItems.value.keys());

    if (selectAll) {
      // eslint-disable-next-line no-param-reassign
      selectedIds.value = new Set([...selectedIds.value, ...currentPageIds]);
    } else {
      // eslint-disable-next-line no-param-reassign
      selectedIds.value = new Set(
        Array.from(selectedIds.value).filter((id) => !currentPageIds.has(id))
      );
    }

    if (localPage.value.page !== pagination.value.page) {
      localPage.value = pagination.value;
    }
  }

  function toggleItemSelection(id: Id, isSelected: boolean): void {
    selectedIds.value[isSelected ? "add" : "delete"](id);
  }

  return {
    isAllSelected,
    isIndeterminate,

    toggleSelectAll,
    toggleItemSelection,
  };
}
